import { themeVar } from '@/styles/theme';
import styled, { keyframes } from 'styled-components';

import { AiAssistantGetAiRunStatusQuery } from '@/__generated__/graphql';
import client from '@/apollo-client';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { ArrowDown } from '@/assets/icons/info-icons/arrowDown';
import { CloseInfoIcon } from '@/assets/icons/info-icons/closeInfo';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { AiAssistantIcon } from '@/assets/static/icons/ai-assistant';
import { DocumentIcon } from '@/assets/static/icons/document';
import { SendIcon } from '@/assets/static/icons/send';
import { ArrowUp } from '@/assets/static/icons/todo-page/arrow-up';
import { UserChatIcon } from '@/assets/static/icons/user-chat';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import Tabs, { ITab, TabsLine } from '@/components/basicComponents/tabs';
import FileNotSupported from '@/components/dueDiligence-page/modals/fileNotSupported';
import { Loader } from '@/components/loaders/loader';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import axios from 'axios';
import { format } from 'date-fns';
import React, { KeyboardEventHandler, RefObject, useEffect, useRef, useState } from 'react';
import { EllipsisWithTooltip } from '../basicComponents/ellipsisWithTooltip';
import { Tooltip } from '../basicComponents/tooltip';
import { GET_SETTINGS_QUERY } from '../settings-page/queries';
import { Citation } from './citation';
import { FileSelector } from './fileSelector';
import AiSuggestions from './modals/aiSuggestions';
import AiAssistantProgressBar from './progressBar';
import {
  CREATE_AI_ASSET_MUTATION,
  GENERATE_ANSWERS_MUTATION,
  GENERATE_CHAT_REPLY_MUTATION,
  GET_AI_ASSET_UPLOAD_URL_QUERY,
  GET_AI_ASSETS_QUERY,
  GET_AI_CHAT_THREAD_QUERY,
  GET_AI_RUN_STATUS_QUERY,
  PROCESS_AI_ASSET_MUTATION
} from './queries';
import { IAiAssets, IAiRunPromptAnswerCitation } from './types';
import { AiPaneZeroState } from './zeroState';

export const aiPaneTabsOnEditFieldPages: ITab[] = [
  { value: 'Citations', amount: null },
  { value: 'Free Chat', amount: null }
];

const defaultAiPaneTabs: ITab[] = [
  { value: 'Autofill', amount: null },
  { value: 'Free Chat', amount: null }
];

const allowFileExtensions = (files: FileList | null) => {
  return Array.from(files || []).filter((file: File) => {
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if ([...fileExtensions.map((item) => item.toLowerCase())].includes(fileExt || '')) {
      return file;
    }
  });
};

const fileExtensions = ['PDF'];

interface AiAssistantProps {
  isAiAssistantOpen: boolean;
  ids: {
    recordId: string;
    investmentId: string;
    funnelId?: string;
  };
  type: string;
  filesForAiAssistant: IAiAssets[];
  citationsTabData?: {
    id: string;
    fieldName: string;
    citations: IAiRunPromptAnswerCitation[];
  }[];
  tabs?: ITab[];
  onFileUploadComplete?: (assetKey: string) => Promise<void>;
  handleAiRunStatusRefetch?: (getAIRunStatusRefetch: () => Promise<ApolloQueryResult<AiAssistantGetAiRunStatusQuery>>) => void;
  handleFilesForAiAssistant: (files: IAiAssets[]) => void;
  handleGenerateAnswersLoadingChange: (loadingStatus: boolean, showCompleteIcon: boolean) => void;
  mainQueryRefetch: () => Promise<ApolloQueryResult<any>>;
  openAdvancedOptions: () => void;
  onClose: () => void;
}

export const AiAssistant = ({
  isAiAssistantOpen,
  ids,
  type,
  filesForAiAssistant,
  tabs,
  citationsTabData,
  onFileUploadComplete,
  handleAiRunStatusRefetch,
  handleFilesForAiAssistant,
  handleGenerateAnswersLoadingChange,
  mainQueryRefetch,
  openAdvancedOptions,
  onClose
}: AiAssistantProps) => {
  const { isMobile, isTablet } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [drag, setDrag] = useState(false);
  const [isStatusDropDownOpen, setIsStatusDropDownOpen] = useState(false);
  const [showDragonDrop, setShowDragonDrop] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [aiRunStatus, setAiRunStatus] = useState<AiAssistantGetAiRunStatusQuery['getAIRunStatus'] | null>(null);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'ai-suggestions' });
  const [aiPaneTabs, setAiPaneTabs] = useState<ITab[]>(defaultAiPaneTabs);
  const [activeTab, setActiveTab] = useState(aiPaneTabs[0].value);
  const [chatPrompt, setChatPrompt] = useState('');
  const [chatHistory, setChatHistory] = useState<{ prompt: string; reply: string; citations: IAiRunPromptAnswerCitation[] }[]>([
    { prompt: '', reply: '', citations: [] }
  ]);
  const [filesToGenerateAnswers, setFilesToGenerateAnswers] = useState<string[]>([]);
  const [filesForChat, setFilesForChat] = useState<string[]>([]);
  const [selectedProcessingOption, setSelectedProcessingOption] = useState('');

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadFileLoading, setUploadFileLoading] = useState(false);
  const [generateAnswersLoading, setGenerateAnswersLoading] = useState(false);
  const [generateChatReplyLoading, setGenerateChatReplyLoading] = useState(false);

  const [unstructuredAvailablity, setUnstructuredAvailability] = useState({ useUnstructured: false });
  const [progressBarValues, setProgressBarValues] = useState({ currentAnswers: 0, totalAnswers: 0 });

  const contentRef: RefObject<HTMLDivElement> = useRef(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const answerWrapRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const [createAIAsset] = useMutation(CREATE_AI_ASSET_MUTATION);
  const [processAIAsset] = useMutation(PROCESS_AI_ASSET_MUTATION);
  const [generateAnswers] = useMutation(GENERATE_ANSWERS_MUTATION);
  const [generateChatReply] = useMutation(GENERATE_CHAT_REPLY_MUTATION);

  useEffect(() => {
    if (!isAiAssistantOpen || aiRunStatus?.status !== 'Completed') {
      setShowSuccessBanner(false);
    }
  }, [isAiAssistantOpen, aiRunStatus?.status]);

  const { data: getAIChatThreadData, refetch: getAIChatThreadRefetch } = useQuery(GET_AI_CHAT_THREAD_QUERY, {
    skip: !ids?.recordId,
    variables: {
      data: {
        type,
        recordId: ids?.recordId
      }
    }
  });

  const { refetch: getAIRunStatusRefetch } = useQuery(GET_AI_RUN_STATUS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      recordId: ids?.recordId,
      documentType: type
    },
    onCompleted: (data) => {
      setAiRunStatus(data.getAIRunStatus);
      setFilesToGenerateAnswers(data?.getAIRunStatus?.filenames || []);
      setFilesForChat(data?.getAIRunStatus?.filenames || []);
      setProgressBarValues({ currentAnswers: data.getAIRunStatus?.currentAnswers ?? 0, totalAnswers: data.getAIRunStatus?.totalAnswers ?? 0 });

      if (data.getAIRunStatus?.status === 'Completed') {
        setShowSuccessBanner(true);
        mainQueryRefetch();
        aiAssetsRefetch();
        setProgressBarValues({ currentAnswers: 0, totalAnswers: 0 });
      }
    }
  });

  useQuery(GET_SETTINGS_QUERY, {
    variables: {
      type: 'dealAssistantAiConfig'
    },
    onCompleted: (data) => {
      const { useUnstructured } = JSON.parse(data.Settings[0].value);
      setUnstructuredAvailability({ useUnstructured });
      setSelectedProcessingOption(useUnstructured ? '' : 'Text only');
    }
  });

  useEffect(() => {
    if (handleAiRunStatusRefetch) {
      handleAiRunStatusRefetch(getAIRunStatusRefetch);
    }
  }, []);

  useEffect(() => {
    if (getAIChatThreadData?.getChatThread?.answers) {
      setChatHistory(
        getAIChatThreadData.getChatThread.answers.map((answer: any) => ({
          prompt: answer.prompt,
          reply: answer.answer,
          citations: answer.citations
        }))
      );
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = contentRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [getAIChatThreadData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      getAIRunStatusRefetch();
      console.log('timeout');
    }, 10000);

    if (aiRunStatus?.status !== 'In Progress') {
      clearInterval(intervalId);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [aiRunStatus?.status]);

  useEffect(() => {
    setShowDragonDrop(false);
  }, [activeTab]);

  useEffect(() => {
    if (tabs?.length) {
      const hasCitations = citationsTabData?.some((item) => item.citations.length > 0);
      if (hasCitations) {
        setActiveTab(tabs[0].value);
        setAiPaneTabs(tabs);
        return;
      }

      setActiveTab('Free Chat');
      setAiPaneTabs([{ value: 'Free Chat', amount: null }]);
      return;
    }
    setActiveTab(defaultAiPaneTabs[0].value);
    setAiPaneTabs(defaultAiPaneTabs);
  }, [tabs, citationsTabData]);

  useEffect(() => {
    if (contentRef.current) {
      const contentElement = contentRef.current;

      requestAnimationFrame(() => {
        contentElement.scrollTop = contentElement.scrollHeight;
      });
    }
  }, [activeTab]);

  const onDropHandler = async (e: React.DragEvent<HTMLDivElement>, selectedProcessingOption?: string) => {
    e.preventDefault();
    const uploadFiles = e.dataTransfer.files;

    setDrag(false);
    setShowDragonDrop(false);
    if (!allowFileExtensions(uploadFiles).length) {
      setModalWindow({ isOpen: true, type: 'not-supported' });
      return;
    }
    if (uploadFiles && uploadFiles.length > 0) {
      handleFilesForAiAssistant([
        ...filesForAiAssistant,
        { filename: uploadFiles[0].name, id: null, investmentId: null, status: null, assetKey: null }
      ]);
      await uploadFile(uploadFiles[0], selectedProcessingOption);
    }
  };

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>, selectedProcessingOption?: string) => {
    e.preventDefault();
    const uploadFiles = e.target.files;

    setShowDragonDrop(false);
    if (!allowFileExtensions(uploadFiles).length) {
      setModalWindow({ isOpen: true, type: 'not-supported' });
      return;
    }
    if (uploadFiles && uploadFiles.length > 0) {
      handleFilesForAiAssistant([
        ...filesForAiAssistant,
        { filename: uploadFiles[0].name, id: null, investmentId: null, status: null, assetKey: null }
      ]);
      await uploadFile(uploadFiles[0], selectedProcessingOption);
    }
  };

  useEffect(() => {
    handleGenerateAnswersLoadingChange(generateAnswersLoading || aiRunStatus?.status === 'In Progress', showSuccessBanner);
  }, [generateAnswersLoading, handleGenerateAnswersLoadingChange, showSuccessBanner]);

  const handleGenerateAnswers = async () => {
    try {
      setErrorMessage(null);
      // setGenerateAnswersLoading(true);
      setModalWindow({ type: 'ai-suggestions', isOpen: false });
      setAiRunStatus({ ...aiRunStatus, status: 'In Progress' });

      await generateAnswers({
        variables: {
          data: {
            investmentId: ids?.investmentId,
            recordId: ids?.recordId,
            type,
            filenames: filesToGenerateAnswers
          }
        }
      });
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      // setGenerateAnswersLoading(false);
    }
  };

  const handleGetChatReply = async () => {
    try {
      setErrorMessage(null);
      setGenerateChatReplyLoading(true);

      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      const prompt = chatPrompt;
      setChatPrompt('');
      setChatHistory(
        chatHistory.concat({
          prompt,
          reply: '',
          citations: []
        })
      );
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = contentRef.current.scrollHeight;
        }
      }, 0);

      await generateChatReply({
        variables: {
          data: {
            investmentId: ids?.investmentId,
            filenames: filesForChat,
            type,
            prompts: [chatPrompt],
            contextPrompt: '',
            summaryPrompt: '',
            recordId: ids?.recordId
          }
        }
      });

      await getAIChatThreadRefetch();
      if (contentRef.current) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setGenerateChatReplyLoading(false);
    }
  };

  const handlePressEnter: KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (event.key === 'Enter') {
      if (!event.shiftKey) {
        event.preventDefault();
        handleGetChatReply();
      }
    }
  };

  const handleDdrFileUpload = async (file: File) => {
    const { data: aiAssetData } = await client.query({
      query: GET_AI_ASSET_UPLOAD_URL_QUERY,
      variables: {
        data: {
          contentType: file.type,
          type,
          investmentId: ids?.investmentId,
          recordId: ids?.recordId,
          filename: file.name
        }
      }
    });

    const { url, assetKey } = aiAssetData.getDealAssistantAIAssetUploadUrl;

    await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });

    await createAIAsset({
      variables: {
        data: {
          investmentId: ids?.investmentId,
          recordId: ids?.recordId,
          assetKey,
          status: 'CREATED',
          type,
          filename: file.name
        }
      }
    });

    return { assetKey, filename: file.name };
  };

  const handleInvestmentFileUpload = async (file: File): Promise<{ assetKey: string; filename: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await axios.post(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/investment-offering-materials-attachment/investment/${ids?.investmentId}/document-record/${ids?.recordId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    return { assetKey: data.assetKey, filename: data.fileName };
  };

  const uploadFile = async (file: File, processingOption?: string) => {
    try {
      setErrorMessage(null);
      setUploadFileLoading(true);

      const { assetKey, filename } = type === 'ddr' ? await handleDdrFileUpload(file) : await handleInvestmentFileUpload(file);

      await processAIAsset({
        variables: {
          data: {
            investmentId: ids?.investmentId,
            recordId: ids?.recordId,
            filename,
            type,
            assetKey,
            processingOption: processingOption
          }
        }
      });

      if (onFileUploadComplete) {
        await onFileUploadComplete(assetKey);
      }
      await aiAssetsRefetch();
    } catch (error) {
      console.log('error', error);
      setErrorMessage(defaultErrorMessage);
      await aiAssetsRefetch();
    } finally {
      setUploadFileLoading(false);
    }
  };

  const { refetch: aiAssetsRefetch } = useQuery(GET_AI_ASSETS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      data: {
        recordId: ids?.recordId
      }
    },
    onCompleted: (data) => {
      const filteredAssets = data.getDealAssistentAIAssets.filter(
        (asset) => typeof asset.assetKey === 'string' && asset.assetKey.toLowerCase().endsWith('.pdf')
      );
      handleFilesForAiAssistant(filteredAssets);
    }
  });

  const scrollToBottom = (index: number, scrollBehavior: ScrollBehavior) => {
    setTimeout(
      () => {
        if (answerWrapRefs.current[index]) {
          answerWrapRefs.current[index].scrollIntoView({ behavior: scrollBehavior, block: 'end', inline: 'nearest' });
        }
      },
      scrollBehavior === 'smooth' ? 200 : 0
    );
  };

  const scrollChatBottomByDefault = () => {
    if (contentRef.current) {
      const contentElement = contentRef.current;
      contentElement.scrollTop = contentElement.scrollHeight;
    }
  };

  if (!filesForAiAssistant.length) {
    return (
      <>
        {modalWindow.type === 'not-supported' && (
          <FileNotSupported isOpen={modalWindow.isOpen} onClose={() => setModalWindow({ ...modalWindow, isOpen: false })} />
        )}
        <AiPaneWrapper isAiAssistantOpen={isAiAssistantOpen} isMobile={isMobile} isTablet={isTablet} onAnimationEnd={scrollChatBottomByDefault}>
          <AiPaneHeader>
            <HeaderTitle>
              <AiAssistantIcon width={28} height={28} fill={themeVar('font.strong')} />
              AI Assistant
            </HeaderTitle>
            <CloseBtn onClick={onClose}>
              <CloseInfoIcon width={14} height={14} fill={themeVar('action.default')} />
            </CloseBtn>
          </AiPaneHeader>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <Tabs tabs={aiPaneTabs} activeTab={activeTab} setActiveTab={setActiveTab} />
          <TabsLine />
          <AiPaneZeroState
            useUnstructured={unstructuredAvailablity.useUnstructured}
            selectedProcessingOption={selectedProcessingOption}
            fileExtensions={fileExtensions}
            setSelectedProcessingOption={setSelectedProcessingOption}
            handleUploadFile={handleUploadFile}
            onDropHandler={onDropHandler}
            drag={drag}
            setDrag={setDrag}
          />
        </AiPaneWrapper>
      </>
    );
  }

  if (activeTab === 'Autofill') {
    return (
      <AiPaneWrapper isAiAssistantOpen={isAiAssistantOpen} isMobile={isMobile} isTablet={isTablet} onAnimationEnd={scrollChatBottomByDefault}>
        {modalWindow.isOpen && modalWindow.type === 'ai-suggestions' && (
          <AiSuggestions
            isOpen={modalWindow.isOpen}
            filesForAiAssistant={filesForAiAssistant}
            selectedFiles={filesToGenerateAnswers}
            setSelectedFiles={setFilesToGenerateAnswers}
            handleGenerateAnswers={handleGenerateAnswers}
            onClose={() => setModalWindow({ type: 'ai-suggestions', isOpen: false })}
          />
        )}
        {modalWindow.type === 'not-supported' && (
          <FileNotSupported isOpen={modalWindow.isOpen} onClose={() => setModalWindow({ ...modalWindow, isOpen: false })} />
        )}
        <div>
          <AiPaneHeader>
            <HeaderTitle>
              <AiAssistantIcon width={28} height={28} fill={themeVar('font.strong')} />
              AI Assistant
            </HeaderTitle>
            <CloseBtn onClick={onClose}>
              <CloseInfoIcon width={14} height={14} fill={themeVar('action.default')} />
            </CloseBtn>
          </AiPaneHeader>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <Tabs tabs={aiPaneTabs} activeTab={activeTab} setActiveTab={setActiveTab} />
          <TabsLine />
          {showSuccessBanner && (
            <BannerWrapper>
              <Banner
                icon={<ApprovedIcon fill={themeVar('context.light')} />}
                title="Success!"
                description="Suggestions ready for review."
                bgColor={themeVar('context.success')}
                onClose={() => setShowSuccessBanner(false)}
              />
            </BannerWrapper>
          )}
        </div>
        <AiPaneBodyWrapper>
          <DocumentsContainer>
            {aiRunStatus && aiRunStatus.status === 'Completed' && (
              <>
                <SelectedDocument withBorderBottom onClick={() => setIsStatusDropDownOpen(!isStatusDropDownOpen)}>
                  {aiRunStatus?.startedBy?.firstName && aiRunStatus?.startedBy?.lastName ? (
                    <div>
                      Suggestions last generated:
                      <BoldText> {` ${aiRunStatus?.completedAt ? format(new Date(aiRunStatus.completedAt), 'LLL d, yyyy') : ''}`}</BoldText> by
                      <BoldText>{` ${aiRunStatus.startedBy.firstName} ${aiRunStatus.startedBy.lastName}`}</BoldText>
                    </div>
                  ) : (
                    <div>
                      Suggestions last generated:
                      <BoldText> {` ${aiRunStatus?.completedAt ? format(new Date(aiRunStatus.completedAt), 'LLL d, yyyy') : ''}`}</BoldText>
                    </div>
                  )}

                  {isStatusDropDownOpen ? (
                    <ArrowUp fill={themeVar('font.action')} />
                  ) : (
                    <ArrowDown style={{ width: '28px', height: '28px', padding: '7px' }} fill={themeVar('font.action')} />
                  )}
                </SelectedDocument>
                <DocumentsListWrapper isDropDownOpen={isStatusDropDownOpen}>
                  <ExpandableWrapper isDropDownOpen={isStatusDropDownOpen}>
                    <UploadedDocumnts>
                      {aiRunStatus.filenames.map((file, index) => (
                        <UploadedDocumentItem key={file + index} id={'input'}>
                          <CheckBoxWrap>
                            <DocumentIconWrapper>
                              <DocumentIcon fill={themeVar('font.weak')} />
                            </DocumentIconWrapper>
                            <EllipsisWithTooltip title={file} forceRecalculate={isAiAssistantOpen}>
                              {file}
                            </EllipsisWithTooltip>
                          </CheckBoxWrap>
                        </UploadedDocumentItem>
                      ))}
                    </UploadedDocumnts>
                  </ExpandableWrapper>
                </DocumentsListWrapper>
              </>
            )}
            {aiRunStatus && aiRunStatus.status === 'In Progress' && (
              <>
                <SelectedDocument>
                  {aiRunStatus?.startedBy?.firstName && aiRunStatus?.startedBy?.lastName ? (
                    <div>
                      Suggestions generation in progress:
                      <BoldText> {` ${aiRunStatus?.startedAt ? format(new Date(aiRunStatus.startedAt), 'LLL d, yyyy') : ''}`}</BoldText> by
                      <BoldText>{` ${aiRunStatus.startedBy.firstName} ${aiRunStatus.startedBy.lastName}`}</BoldText>
                    </div>
                  ) : (
                    <div>
                      Suggestions generation in progress:
                      <BoldText> {` ${aiRunStatus?.startedAt ? format(new Date(aiRunStatus.startedAt), 'LLL d, yyyy') : ''}`}</BoldText>
                    </div>
                  )}
                </SelectedDocument>
                <TextDescription>This processing time will vary based on the size and number of documents that were selected.</TextDescription>
              </>
            )}
            <FileSelector
              filesForAiAssistant={filesForAiAssistant}
              selectedFiles={filesToGenerateAnswers}
              fileExtensions={fileExtensions}
              showDragonDrop={showDragonDrop}
              drag={drag}
              scrollable={false}
              isAiAssistantOpen={isAiAssistantOpen}
              generateAnswersLoading={generateAnswersLoading || aiRunStatus?.status === 'In Progress'}
              setSelectedFiles={setFilesToGenerateAnswers}
              setShowDragonDrop={setShowDragonDrop}
              setDrag={setDrag}
              handleUploadFile={handleUploadFile}
              onDropHandler={onDropHandler}
            />
          </DocumentsContainer>
          <SuggestionButtonWrapper>
            <Button
              onClick={() => setModalWindow({ isOpen: true, type: 'ai-suggestions' })}
              size="lg"
              width="100%"
              styleType={
                uploadFileLoading || !filesToGenerateAnswers.length || generateAnswersLoading || aiRunStatus?.status === 'In Progress'
                  ? 'disabled'
                  : 'default'
              }
            >
              {generateAnswersLoading || aiRunStatus?.status === 'In Progress' ? (
                <>
                  <CustomLoader size={28} />
                  Suggesting Answers For Selected Documents
                </>
              ) : (
                <>
                  <AiAssistantIcon
                    width={28}
                    height={28}
                    fill={uploadFileLoading || !filesToGenerateAnswers.length ? themeVar('font.disabled') : themeVar('context.light')}
                  />
                  Suggest Answers
                </>
              )}
            </Button>
            {/* {user && user.roles.includes('Super Admin') && <AdvancedOptions onClick={openAdvancedOptions}>Advanced Options</AdvancedOptions>} */}
            {(generateAnswersLoading || aiRunStatus?.status === 'In Progress') && (
              <AiAssistantProgressBar currentAnswers={progressBarValues.currentAnswers} totalAnswers={progressBarValues.totalAnswers} />
            )}
          </SuggestionButtonWrapper>
        </AiPaneBodyWrapper>
      </AiPaneWrapper>
    );
  }

  if (activeTab === 'Citations') {
    return (
      <AiPaneWrapper isAiAssistantOpen={isAiAssistantOpen} isMobile={isMobile} isTablet={isTablet} onAnimationEnd={scrollChatBottomByDefault}>
        <AiPaneHeader>
          <HeaderTitle>
            <AiAssistantIcon width={28} height={28} fill={themeVar('font.strong')} />
            AI Assistant
          </HeaderTitle>
          <CloseBtn onClick={onClose}>
            <CloseInfoIcon width={14} height={14} fill={themeVar('action.default')} />
          </CloseBtn>
        </AiPaneHeader>
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <Tabs tabs={aiPaneTabs} activeTab={activeTab} setActiveTab={setActiveTab} />
        <TabsLine />
        <CitationsWrapper>
          {citationsTabData?.map((item) => <Citation key={item.id} ids={ids} fieldName={item.fieldName} citations={item.citations} />)}
        </CitationsWrapper>
      </AiPaneWrapper>
    );
  }

  if (activeTab === 'Free Chat') {
    return (
      <AiPaneWrapper isAiAssistantOpen={isAiAssistantOpen} isMobile={isMobile} isTablet={isTablet} onAnimationEnd={scrollChatBottomByDefault}>
        {modalWindow.type === 'not-supported' && (
          <FileNotSupported isOpen={modalWindow.isOpen} onClose={() => setModalWindow({ ...modalWindow, isOpen: false })} />
        )}
        <FreeChatWrapper>
          <div>
            <AiPaneHeader>
              <HeaderTitle>
                <AiAssistantIcon width={28} height={28} fill={themeVar('font.strong')} />
                AI Assistant
              </HeaderTitle>
              <CloseBtn onClick={onClose}>
                <CloseInfoIcon width={14} height={14} fill={themeVar('action.default')} />
              </CloseBtn>
            </AiPaneHeader>
            {errorMessage && (
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            )}
            <Tabs tabs={aiPaneTabs} activeTab={activeTab} setActiveTab={setActiveTab} />
            <TabsLine />
          </div>
          <AiPaneBodyWrapper>
            <ChatWrapper ref={contentRef}>
              <FileSelectorWrapper>
                <FileSelector
                  filesForAiAssistant={filesForAiAssistant}
                  selectedFiles={filesForChat}
                  fileExtensions={fileExtensions}
                  showDragonDrop={showDragonDrop}
                  drag={drag}
                  scrollable={false}
                  isAiAssistantOpen={isAiAssistantOpen}
                  generateAnswersLoading={generateAnswersLoading || aiRunStatus?.status === 'In Progress'}
                  setSelectedFiles={setFilesForChat}
                  setShowDragonDrop={setShowDragonDrop}
                  setDrag={setDrag}
                  handleUploadFile={handleUploadFile}
                  onDropHandler={onDropHandler}
                />
              </FileSelectorWrapper>
              {chatHistory.map((chatItem, index) => (
                <React.Fragment key={index}>
                  {chatItem.prompt ? (
                    <ChatItem>
                      <div>
                        <UserChatIcon fill={themeVar('border.base')} style={{ marginTop: '7px' }} />
                      </div>
                      {chatItem.prompt}
                    </ChatItem>
                  ) : (
                    <></>
                  )}
                  {chatItem.reply ? (
                    <ChatAnswerWrap
                      ref={(ref) => {
                        answerWrapRefs.current[index] = ref;
                      }}
                    >
                      <ChatItem>
                        <div>
                          <AiAssistantIcon width={28} height={28} style={{ margin: '8px 0 0 10px' }} fill={themeVar('font.base')} />
                        </div>
                        {chatItem.reply}
                      </ChatItem>
                      <Citation
                        ids={ids}
                        fieldName="Citations"
                        citations={chatItem?.citations || []}
                        defaultShowCitations={false}
                        scrollToBottom={(scrollBehavior) => scrollToBottom(index, scrollBehavior)}
                      />
                    </ChatAnswerWrap>
                  ) : (
                    <></>
                  )}
                </React.Fragment>
              ))}
            </ChatWrapper>
          </AiPaneBodyWrapper>
          <ChatTextareaWrapper>
            {generateChatReplyLoading ? (
              <SuggestingAnswersWrap>
                <CustomLoader size={28} />
                Suggesting Answers For Selected Documents
              </SuggestingAnswersWrap>
            ) : (
              <FullWidthTooltip title="You must select a file before asking a question." disableTooltip={!!filesForChat.length}>
                <CustomStyledTextarea
                  isDisabled={!filesForChat.length || generateChatReplyLoading}
                  ref={textareaRef}
                  placeholder="Ask a question"
                  value={chatPrompt}
                  onChange={(event) => {
                    setChatPrompt(event.target.value);

                    event.target.style.height = 'auto';
                    event.target.style.height = `${event.target.scrollHeight < 45 ? '44.4px' : event.target.scrollHeight}px`;
                  }}
                  onKeyDown={handlePressEnter}
                  disabled={!filesForChat.length}
                  rows={1}
                />
                <SendBtn isDisabled={!filesForChat.length} onClick={filesForChat.length ? handleGetChatReply : () => null}>
                  <SendIcon fill={filesForChat.length ? themeVar('action.primary') : themeVar('font.disabled')} />
                </SendBtn>
              </FullWidthTooltip>
            )}
          </ChatTextareaWrapper>
        </FreeChatWrapper>
      </AiPaneWrapper>
    );
  }

  return (
    <AiPaneWrapper isAiAssistantOpen={isAiAssistantOpen} isMobile={isMobile} isTablet={isTablet} onAnimationEnd={scrollChatBottomByDefault}>
      <AiPaneHeader>
        <HeaderTitle>
          <AiAssistantIcon width={28} height={28} fill={themeVar('font.strong')} />
          AI Assistant
        </HeaderTitle>
        <CloseBtn onClick={onClose}>
          <CloseInfoIcon width={14} height={14} fill={themeVar('action.default')} />
        </CloseBtn>
      </AiPaneHeader>
      <Banner
        icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
        title="Error"
        description="Something Went Wrong"
        bgColor={themeVar('context.error')}
      />
    </AiPaneWrapper>
  );
};

const displayPane = keyframes`
  to {
    max-width: 600px;
  }
`;

const displayMobilePane = keyframes`
  to {
    max-width: 100%;
  }
`;

const AiPaneWrapper = styled.div<{ isMobile: boolean; isTablet: boolean; isAiAssistantOpen: boolean }>`
  position: sticky;
  margin: 0 auto;
  height: 100vh;
  max-width: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '100%' : '0px')};
  width: 100%;
  display: ${({ isAiAssistantOpen }) => (isAiAssistantOpen ? 'flex' : 'none')};
  flex-direction: column;
  top: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '50px' : 0)};
  background: ${themeVar('layer.1')};
  box-shadow: -14px 0px 12px -10px rgba(0, 0, 0, 0.15);
  z-index: 101;
  padding: 0 20px;
  animation: ${({ isMobile, isTablet }) => (isMobile || isTablet ? displayMobilePane : displayPane)} 0.2s linear forwards;
`;

const AiPaneHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  font-size: 28px;
  color: ${themeVar('font.strong')};
`;

const CloseBtn = styled.div`
  display: flex;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  border: 1px solid ${themeVar('action.default')};
  background: ${themeVar('layer.base')};
`;

const AiPaneBodyWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-top: 40px;
`;

const FreeChatWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const DocumentsContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: none;
`;

const SelectedDocument = styled.div<{ withBorderBottom?: boolean }>`
  margin: 0 20px;
  padding: 10px 0;
  border-bottom: ${({ withBorderBottom }) => (withBorderBottom ? `1px solid ${themeVar('border.base')}` : 'none')};
  font-weight: 400;
  font-size: 16px;
  color: ${themeVar('font.weak')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const TextDescription = styled.div`
  padding: 0 20px;
  color: ${themeVar('font.base')};
  font-family: Blinker;
  font-size: 16px;
  font-weight: 400;
  margin-bottom: 20px;
`;

const BoldText = styled.span`
  font-weight: 700;
`;

const DocumentsListWrapper = styled.div<{ isDropDownOpen: boolean }>`
  display: grid;
  grid-template-rows: ${({ isDropDownOpen }) => (isDropDownOpen ? '1fr' : '0fr')};
  overflow: ${({ isDropDownOpen }) => (isDropDownOpen ? 'visible' : 'hidden')};
  transition: grid-template-rows 300ms;
`;

const ExpandableWrapper = styled.div<{ isDropDownOpen: boolean }>`
  min-height: 0;
  opacity: ${({ isDropDownOpen }) => (isDropDownOpen ? '1' : '0')};
`;

const UploadedDocumnts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 15px 20px 0 35px;
  margin-bottom: 5px;
`;

const CheckBoxWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  font-size: 16px;
  word-break: break-word;
`;

const UploadedDocumentItem = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ disabled }) => (disabled ? themeVar('font.disabled') : themeVar('font.base'))};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

  #input {
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  }
`;

const CustomLoader = styled(Loader)`
  padding: 0;
`;

const SuggestionButtonWrapper = styled.div`
  padding: 20px 0;
`;

const ChatTextareaWrapper = styled.div`
  position: sticky;
  bottom: 0;
  padding: 20px 0 15px 0;
  border-top: 1px solid ${themeVar('border.base')};
  display: flex;
  background-color: ${themeVar('layer.1')};
`;

const SuggestingAnswersWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  color: ${themeVar('font.disabled')};
  font-size: 19px;
  font-weight: 600;
  padding-bottom: 5px;
`;

const FullWidthTooltip = styled(Tooltip)`
  width: 100%;
`;

const CustomStyledTextarea = styled.textarea<{ isDisabled: boolean }>`
  width: 100%;
  border-radius: 4px;
  padding: 10px 8px;
  font-family: 'Blinker';
  font-weight: 300;
  font-size: 16px;
  line-height: 22.4px;
  width: 100%;
  padding: 10px 45px 10px 10px;
  height: 44.4px;
  max-height: 70px;
  box-sizing: border-box;
  resize: none;
  color: ${themeVar('font.base')};
  overscroll-behavior: none;
  background-color: ${({ isDisabled }) => (isDisabled ? themeVar('action.disabled') : themeVar('layer.1'))};

  &:focus {
    outline: none;
  }
`;

const SendBtn = styled.div<{ isDisabled: boolean }>`
  position: absolute;
  right: 20px;
  bottom: 26px;
  cursor: ${({ isDisabled }) => (isDisabled ? 'auto' : 'pointer')};
`;

const FileSelectorWrapper = styled.div`
  padding-bottom: 20px;
`;

const ChatWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overscroll-behavior: none;
  position: relative;
`;

const ChatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 300;
  font-size: 16px;
  color: ${themeVar('font.base')};
  padding: 5px;
`;

const ChatAnswerWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AdvancedOptions = styled.span`
  margin-top: 10px;
  display: flex;
  justify-content: center;
  padding: 10px 0;
  cursor: pointer;
  color: ${themeVar('font.action')};
  font-weight: 600;
`;

const CitationsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: calc(100% - 160px);
  overflow-y: auto;
  overscroll-behavior: none;
  padding-top: 20px;
`;

const BannerWrapper = styled.div`
  margin-top: 10px;
`;

const DocumentIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
